import { useCallback, type ReactNode } from 'react';
import { cn, stateTone } from '../cn';
import { useSwipeTabs } from '../hooks/useSwipeTabs';
import { Avatar } from '../components/Avatar';
import { Button } from '../components/Button';
import { Logo } from '../components/Logo';
import { NavItem, NavSection } from '../components/NavItem';
import { StatusPill } from '../components/StatusPill';
import { TabBarItem } from '../components/TabBarItem';

export interface NavEntry {
  key: string;
  label: string;
  meta?: ReactNode;
  active?: boolean;
  onSelect: () => void;
  href?: string;
}
export interface NavGroup {
  label: string;
  items: NavEntry[];
  /** Device card shown under the group label (cloud "Device" group). */
  deviceCard?: { name: string; event: string; state: string };
}
export interface ShellProps {
  logoSub: string;
  groups: NavGroup[];
  user?: { name: string; role: string };
  onSignOut?: () => void;
  signOutLabel?: string;
  /** Desktop breadcrumb: "Device / CC-01 / Live". */
  crumb: string;
  /** Mono header items on the right (relay status, clock). */
  headerItems?: ReactNode;
  theme: 'dark' | 'light';
  onToggleTheme: () => void;
  /** Mobile header. */
  mobileTitle: string;
  /** Device name (never an id) + state. */
  mobileDevice?: { name: string; state: string; onClick: () => void };
  /** `preview`: the tab's page, rendered beside the current one while a swipe is in progress. */
  tabs: { key: string; label: string; active?: boolean; onSelect: () => void; badge?: boolean; preview?: ReactNode; /** Remembered scroll offset of that page, so the preview shows it where the user left it. */ scrollY?: number }[];
  children: ReactNode;
}

/**
 * One tree, two layouts via the 760 px breakpoint:
 *  desktop: 224 px sticky sidebar (bg2, line border) + 52 px blurred sticky header + content
 *  mobile:  430 px max column with line borders, 52 px header, bottom 5-tab bar (54 px)
 */
export function Shell({ logoSub, groups, user, onSignOut, signOutLabel = 'Sign out', crumb, headerItems, theme, onToggleTheme, mobileTitle, mobileDevice, tabs, children }: ShellProps) {
  // Phone: the page follows a horizontal swipe between the bottom tabs; the page itself only scrolls vertically.
  const activeTab = tabs.findIndex((t) => t.active);
  const selectTab = useCallback((i: number) => tabs[i]?.onSelect(), [tabs]);
  const swipe = useSwipeTabs({ count: tabs.length, index: activeTab, onChange: selectTab });
  return (
    <div className={cn('mx-auto flex min-h-screen max-w-mobile flex-col border-x border-line bg-bg', 'md:grid md:max-w-none md:grid-cols-[224px_minmax(0,1fr)] md:border-0')} style={{ overflowX: 'clip' }}>
      {/* ---------------- desktop sidebar ---------------- */}
      <aside className="sticky top-0 hidden h-screen min-h-0 flex-col overflow-y-auto border-r border-line bg-bg2 md:flex">
        <div className="border-b border-line px-4 pb-4 pt-[18px]">
          <Logo sub={logoSub} />
        </div>
        {groups.map((g, gi) => (
          <div key={g.label} className={cn('flex flex-col gap-[2px] px-2.5', gi === 0 ? 'pt-3.5' : 'pt-[22px]')}>
            <NavSection>{g.label}</NavSection>
            {g.deviceCard && (
              <div className="mb-1.5 flex items-center justify-between gap-2 rounded-lg border border-line2 bg-bg px-2.5 py-2.5">
                <div className="flex min-w-0 flex-col gap-1">
                  <span className="text-md font-medium leading-none">{g.deviceCard.name}</span>
                  <span className="truncate font-mono text-xs font-normal tracking-[.04em] text-fg4">{g.deviceCard.event}</span>
                </div>
                <StatusPill tone={stateTone(g.deviceCard.state)}>{g.deviceCard.state}</StatusPill>
              </div>
            )}
            {g.items.map((n) => (
              <NavItem key={n.key} label={n.label} meta={n.meta} active={n.active} onClick={n.onSelect} href={n.href} />
            ))}
          </div>
        ))}
        <div className="sticky bottom-0 mt-auto flex flex-none items-center justify-between gap-2.5 border-t border-line bg-bg2 px-4 py-3.5">
          {user && (
            <div className="flex min-w-0 items-center gap-2.5">
              <Avatar name={user.name} />
              <div className="flex min-w-0 flex-col gap-[3px]">
                <span className="truncate text-base font-medium leading-none">{user.name}</span>
                <span className="font-mono text-xs font-normal uppercase text-fg4">{user.role}</span>
              </div>
            </div>
          )}
          {onSignOut && (
            <Button size="xs" onClick={onSignOut} aria-label="Sign out">
              {signOutLabel}
            </Button>
          )}
        </div>
      </aside>

      {/* ---------------- main ---------------- */}
      <main className="flex min-w-0 flex-1 flex-col md:block">
        {/* desktop header */}
        <header className="sticky top-0 z-[5] hidden h-header items-center justify-between border-b border-line bg-header px-8 backdrop-blur-header md:flex">
          <span className="micro text-sm text-fg3">{crumb}</span>
          <div className="flex items-center gap-[22px]">
            {headerItems}
            <Button variant="ghost" size="xs" mono onClick={onToggleTheme} aria-label="Toggle theme">
              {theme === 'light' ? 'DARK' : 'LIGHT'}
            </Button>
          </div>
        </header>
        {/* mobile header */}
        <header className="sticky top-0 z-[6] flex h-header items-center justify-between border-b border-line bg-header px-4 backdrop-blur-header md:hidden" style={{ paddingTop: 'env(safe-area-inset-top)', height: 'calc(52px + env(safe-area-inset-top))' }}>
          <span className="text-2xl font-semibold tracking-[-.01em]">{mobileTitle}</span>
          {mobileDevice && (
            <button type="button" onClick={mobileDevice.onClick} className="flex h-8 items-center gap-2 rounded-lg border border-border bg-transparent px-2.5 font-mono text-sm font-medium tracking-meta text-fg">
              <span className="max-w-[160px] truncate">{mobileDevice.name}</span>
              <StatusPill tone={stateTone(mobileDevice.state)}>{mobileDevice.state}</StatusPill>
            </button>
          )}
        </header>
        <div ref={swipe.ref} style={swipe.style} {...swipe.handlers} className="flex min-w-0 flex-1 flex-col md:block">
          {children}
        </div>
        {/* The neighbouring tab's page rides alongside during a swipe (phone only): fixed between the header and the bar. */}
        {swipe.neighbour && tabs[swipe.neighbour.index]?.preview && (
          <div
            aria-hidden
            className="pointer-events-none fixed inset-x-0 z-[4] overflow-hidden bg-bg md:hidden"
            style={{ top: 'calc(52px + env(safe-area-inset-top))', bottom: 'calc(54px + env(safe-area-inset-bottom))', ...swipe.neighbour.style }}
          >
            <div className="mx-auto max-w-mobile" style={{ transform: `translateY(${-(tabs[swipe.neighbour.index]!.scrollY ?? 0)}px)` }}>
              {tabs[swipe.neighbour.index]!.preview}
            </div>
          </div>
        )}
      </main>

      {/* mobile tab bar */}
      {/* Equal columns for however many tabs there are; the bar clears the home indicator on phones (safe-area inset). */}
      <nav className="sticky bottom-0 z-[6] mt-auto flex items-stretch border-t border-line bg-header px-1 pb-[env(safe-area-inset-bottom)] backdrop-blur-header md:hidden">
        {tabs.map((t) => (
          <TabBarItem key={t.key} label={t.label} active={t.active} onClick={t.onSelect} badge={t.badge} compact={tabs.length >= 5} />
        ))}
      </nav>
    </div>
  );
}

/** Header mono item: `RELAY OK · cc.example` or `12:00:00 UTC`. */
export function HeaderItem({ children, tabular, className }: { children: ReactNode; tabular?: boolean; className?: string }) {
  return <span className={cn('font-mono text-sm font-medium tracking-meta text-fg3', tabular && 'tabular', className)}>{children}</span>;
}
